/**
 * Cadastros > Regulamentos (specs 017 e 042): a versao atual com os documentos (o geral e um por familia), as anteriores
 * e o "Publicar versao nova". Cada publicacao e uma foto do conjunto inteiro: o que nao foi trocado vem herdado da
 * versao anterior, sem copiar arquivo. Nada se edita; apagar so a atual, e so enquanto nenhuma compra foi feita com ela.
 * Quem decide tudo isso e o backend (numero, herdado, quem pode apagar); a tela mostra e manda.
 */
import { Aviso, Botao, CabecalhoDePagina, Campo, CampoArquivo, CampoTextoLongo, CartaoDaVersao, Carregando, Dialogo, ListaDeEscolha, Pilha, RegulamentoDaCompra, Secao, Segmentado, Selo, Tabela, Vazio, formatarData, formatarDataHora, nomeDaVersao, type DocumentoAceito, type VersaoDeRegulamentos } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { erroDe, useApi, usePapel } from "../api";
import { config } from "../config";
import { useSessao } from "../sessao";
import { DialogoDeAcao } from "./DialogoDeAcao";
import { usePagina } from "../pagina";

type DocumentoCadastrado = { chave: string; titulo: string; familia: string | null; aplica_se: "TODOS" | "FAMILIA"; ordem: number };
type DocumentoDoGrupo = { chave: string; titulo: string; familia: string | null; ordem: number; arquivo: { id: string; nome: string; tipo: string; tamanho: number; sha256: string }; herdado_do_grupo: number | null; pagina?: string };
type Grupo = { numero: number; rotulo: string | null; situacao: "ATUAL" | "DESCONTINUADO"; publicado_em: string; publicado_por: string; compras: number; documentos: DocumentoDoGrupo[]; oculto_do_cliente: { em: string; por: string | null; motivo: string | null } | null };
type Regulamentos = { documentos: DocumentoCadastrado[]; grupos: Grupo[] };

/** O mesmo limite do backend, so pra avisar antes de enviar (quem confere e ele). */
const TAMANHO_MAXIMO = 10 * 1024 * 1024;
const TIPOS = "application/pdf,text/plain,.pdf,.txt";

/** O arquivo publico de um documento numa versao: imutavel, abre sem login. */
export const enderecoDoRegulamento = (caminho: string) => `${config().API_URL.replace(/\/$/, "")}${caminho}`;
/**
 * O link que a tela mostra: a pagina de leitura na area (`pagina`, que o backend monta), formatada como o cliente ve
 * (Vitor, 13/10). Sem ela (backend antigo), o arquivo.
 */
const linkDoDocumento = (d: { pagina?: string; url: string }) => d.pagina ?? enderecoDoRegulamento(d.url);

const naTela = (g: Grupo): VersaoDeRegulamentos => ({
  ...g,
  documentos: g.documentos.map((d) => ({ chave: d.chave, titulo: d.titulo, href: linkDoDocumento({ pagina: d.pagina, url: `/v1/publico/regulamentos/${g.numero}/${encodeURIComponent(d.chave)}` }), arquivo: d.arquivo, herdado_do_grupo: d.herdado_do_grupo })),
});

/** "Geral, Exame e Black" (titulos curtos, so pra frase). */
const juntar = (itens: string[]) => (itens.length <= 1 ? (itens[0] ?? "") : `${itens.slice(0, -1).join(", ")} e ${itens.at(-1)}`);
const curto = (titulo: string) => titulo.replace(/^Regulamento\s*-?\s*/i, "").replace(/^Planos\s+/i, "").replace(/\s+-\s+.*$/, "") || titulo;

export function PaginaRegulamentos() {
  const pag = usePagina("pagina");
  const { ler, apagar, postar } = useApi();
  const { pode } = usePapel();
  const qc = useQueryClient();
  const admin = pode("ADMIN");
  const q = useQuery({ queryKey: ["regulamentos-do-painel"], queryFn: () => ler<Regulamentos>("/v1/admin/regulamentos") });
  const [publicar, setPublicar] = useState(false);
  const [apagarAtual, setApagarAtual] = useState(false);
  const [aberta, setAberta] = useState<Grupo | null>(null);
  const [naArea, setNaArea] = useState<Grupo | null>(null);
  const [motivoDaArea, setMotivoDaArea] = useState("");
  const [feito, setFeito] = useState<string>();
  const recarregar = () => qc.invalidateQueries({ queryKey: ["regulamentos-do-painel"] });

  const documentos = q.data?.documentos ?? [];
  const grupos = q.data?.grupos ?? [];
  const atual = grupos.find((g) => g.situacao === "ATUAL") ?? null;
  const anteriores = grupos.filter((g) => g !== atual);
  // O que volta a ser o atual se a atual for apagada: a maior abaixo dela (o backend faz a mesma escolha).
  const volta = atual ? (anteriores.filter((g) => g.numero < atual.numero).sort((a, b) => b.numero - a.numero)[0] ?? null) : null;

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Regulamentos"
        descricao="O que cada compra aceita no checkout: o regulamento geral e o da família do plano, na versão que estiver no ar na hora da compra."
        acoes={admin && documentos.length > 0 ? <Botao onClick={() => { setFeito(undefined); setPublicar(true); }}>Publicar versão nova</Botao> : undefined}
      />
      <Aviso tipo="info">
        Campanha ou promoção que entra ou sai do regulamento é <strong>versão nova</strong>: suba o PDF já com a mudança e publique. A partir daí, toda compra nova aceita a versão nova, em todos os checkouts, sem editar nenhum. Quem já comprou fica com a versão da compra dele até o plano acabar. Nenhuma versão se edita.
      </Aviso>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      {q.isLoading ? <Carregando compacto texto="Buscando os regulamentos" /> : q.isError ? <Aviso tipo="erro">{erroDe(q.error)}</Aviso> : (
        <>
          {!documentos.length && <Aviso tipo="alerta">Nenhum documento cadastrado neste ambiente (o geral e um por família). Sem eles não dá pra publicar: peça pra carregar a ferramenta de regulamentos do backend.</Aviso>}
          <Secao rotulo="No ar" titulo="Versão atual">
            {atual ? (
              <CartaoDaVersao
                versao={naTela(atual)}
                acoes={admin && atual.compras === 0 ? <Botao variante="perigo" tamanho="pequeno" onClick={() => { setFeito(undefined); setApagarAtual(true); }}>Apagar a versão {atual.numero}</Botao> : undefined}
              />
            ) : (
              <Vazio titulo="Nenhuma versão publicada">Enquanto não houver, o checkout mostra o regulamento da marca. Publique a primeira com o arquivo de cada documento.</Vazio>
            )}
            {atual && atual.compras === 0 && admin && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Subiu o arquivo errado? Enquanto ninguém comprar com esta versão, dá pra apagar e publicar de novo. Na primeira compra, ela fica pra sempre.</span>}
          </Secao>
          <Secao rotulo="Histórico" titulo="Versões anteriores">
            <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Continuam inteiras e abertas: é por elas que são julgados os planos comprados quando estavam no ar.</span>
            <Tabela
              paginacao={pag}
              linhas={anteriores}
              chave={(g) => String(g.numero)}
              aoClicar={setAberta}
              cartao="media"
              vazio={<Vazio titulo="Nenhuma versão anterior">Quando uma versão nova for publicada, a de hoje vem pra cá.</Vazio>}
              colunas={[
                { chave: "versao", rotulo: "Versão", celula: (g) => <><strong>Versão {g.numero}</strong>{g.rotulo && <span className="mtc-tabela__apoio">{g.rotulo}</span>}{g.oculto_do_cliente && <span className="mtc-tabela__apoio"><Selo tom="neutro">Escondida da área do cliente</Selo></span>}</> },
                { chave: "publicada", rotulo: "Publicada", celula: (g) => <span className="mtc-mono">{formatarDataHora(g.publicado_em)}</span> },
                { chave: "documentos", rotulo: "Documentos", celula: (g) => {
                  const novos = g.documentos.filter((d) => !d.herdado_do_grupo).map((d) => curto(d.titulo));
                  const herdados = g.documentos.filter((d) => d.herdado_do_grupo).map((d) => curto(d.titulo));
                  return <>{novos.length > 0 && <span>Novo: {juntar(novos)}</span>}{herdados.length > 0 && <span className="mtc-tabela__apoio">Herdado: {juntar(herdados)}</span>}</>;
                } },
                { chave: "compras", rotulo: "Compras", alinhar: "direita", celula: (g) => <span className="mtc-mono">{g.compras.toLocaleString("pt-BR")}</span> },
                { chave: "acoes", rotulo: "Ações", rotuloOculto: true, celula: (g) => (
                  <>
                    <Botao variante="fantasma" tamanho="pequeno" aria-label={`Ver a versão ${g.numero}`} onClick={() => setAberta(g)}>Ver</Botao>
                    {admin && <Botao variante="fantasma" tamanho="pequeno" aria-label={`${g.oculto_do_cliente ? "Mostrar" : "Esconder"} a versão ${g.numero} na área do cliente`} onClick={(e) => { e.stopPropagation(); setFeito(undefined); setNaArea(g); }}>{g.oculto_do_cliente ? "Mostrar na área" : "Esconder da área"}</Botao>}
                  </>
                ) },
              ]}
            />
          </Secao>
        </>
      )}

      <PublicarVersao
        aberto={publicar}
        aoFechar={() => setPublicar(false)}
        documentos={documentos}
        atual={atual}
        proximo={(grupos[0]?.numero ?? 0) + 1}
        aoConcluir={(n) => { void recarregar(); setFeito(`Versão ${n} publicada. A partir de agora, toda compra nova aceita esta versão, em todos os checkouts. Quem já comprou continua com a da compra.`); window.scrollTo({ top: 0, behavior: "smooth" }); }}
      />
      <DialogoDeAcao
        aberto={apagarAtual && Boolean(atual)}
        aoFechar={() => setApagarAtual(false)}
        titulo={atual ? `Apagar a versão ${atual.numero}?` : ""}
        descricao={atual ? `A versão ${atual.numero} some${volta ? ` e a versão ${volta.numero} volta a ser a atual` : ". Fica sem versão publicada, e o checkout volta a mostrar o regulamento da marca"}. Só dá porque nenhuma compra foi feita com ela. Os arquivos enviados ficam guardados: reenviar o mesmo arquivo cai neles de novo.` : undefined}
        botao="Apagar versão"
        variante="perigo"
        aoEnviar={() => apagar(`/v1/admin/regulamentos/grupos/${atual!.numero}`)}
        aoConcluir={() => { const n = atual?.numero; void recarregar(); setFeito(`Versão ${n} apagada.${volta ? ` A versão ${volta.numero} voltou a ser a atual.` : ""}`); }}
      />
      {/* Esconder da area do cliente (spec 042, 3.9; Vitor, 04/11: "pode remover a versao 1 da visualizacao"): so a tela
          do cliente muda; o aceite, o arquivo e o painel ficam como estao. */}
      <DialogoDeAcao
        aberto={Boolean(naArea)}
        aoFechar={() => setNaArea(null)}
        titulo={naArea ? `${naArea.oculto_do_cliente ? "Mostrar" : "Esconder"} a versão ${naArea.numero} na área do cliente?` : ""}
        descricao={naArea ? (naArea.oculto_do_cliente
          ? `Os ${naArea.compras.toLocaleString("pt-BR")} planos comprados com ela voltam a mostrar o bloco "Regulamento desta compra" na área.`
          : `Os ${naArea.compras.toLocaleString("pt-BR")} planos comprados com ela deixam de mostrar o bloco "Regulamento desta compra" na área. O aceite continua registrado, e aqui no painel tudo segue visível.`) : undefined}
        botao={naArea?.oculto_do_cliente ? "Mostrar na área" : "Esconder da área"}
        podeEnviar={motivoDaArea.trim().length >= 5}
        aoAbrir={() => setMotivoDaArea("")}
        aoEnviar={() => postar(`/v1/admin/regulamentos/grupos/${naArea!.numero}/area-do-cliente`, { oculto: !naArea!.oculto_do_cliente, motivo: motivoDaArea.trim() })}
        aoConcluir={() => { const g = naArea; void recarregar(); setFeito(g ? `Versão ${g.numero} ${g.oculto_do_cliente ? "voltou a aparecer" : "escondida"} na área do cliente.` : undefined); }}
      >
        <CampoTextoLongo rotulo="Motivo" valor={motivoDaArea} aoMudar={setMotivoDaArea} maxLength={500} required placeholder="Ex.: versão antiga, substituída pela versão 2." />
      </DialogoDeAcao>
      <Dialogo aberto={Boolean(aberta)} aoFechar={() => setAberta(null)} titulo={aberta ? nomeDaVersao(aberta.numero, aberta.rotulo) : ""} descricao="Versão descontinuada: nenhuma compra nova entra nela, e quem comprou com ela continua com ela.">
        {aberta && <CartaoDaVersao versao={naTela(aberta)} />}
      </Dialogo>
    </Pilha>
  );
}

type Escolha = { trocar: boolean; arquivo: File | null };

/**
 * "Publicar versao nova" (017, secao 5): uma linha por documento, manter o da versao atual ou enviar um arquivo novo; o
 * rotulo e opcional. Antes de publicar, a frase diz o que troca e o que fica. Na primeira publicacao nao ha de onde
 * herdar, entao todos pedem arquivo. Os arquivos sobem um por vez e so depois a versao e publicada.
 */
function PublicarVersao({ aberto, aoFechar, documentos, atual, proximo, aoConcluir }: { aberto: boolean; aoFechar: () => void; documentos: DocumentoCadastrado[]; atual: Grupo | null; proximo: number; aoConcluir: (numero: number) => void }) {
  const { postar } = useApi();
  const { enviar } = useSessao();
  const [escolhas, setEscolhas] = useState<Record<string, Escolha>>({});
  const [rotulo, setRotulo] = useState("");
  // A resposta da publicacao, lida no fechamento do dialogo (o estado do React ainda nao teria atualizado).
  const publicado = useRef(proximo);
  const doAtual = (chave: string) => atual?.documentos.find((d) => d.chave === chave) ?? null;
  const escolha = (chave: string): Escolha => escolhas[chave] ?? { trocar: !doAtual(chave), arquivo: null };
  const mudar = (chave: string, e: Partial<Escolha>) => setEscolhas({ ...escolhas, [chave]: { ...escolha(chave), ...e } });
  const grande = (f: File | null) => Boolean(f && f.size > TAMANHO_MAXIMO);
  const trocados = documentos.filter((d) => escolha(d.chave).trocar);
  const mantidos = documentos.filter((d) => !escolha(d.chave).trocar);
  const pronto = trocados.length > 0 && trocados.every((d) => escolha(d.chave).arquivo && !grande(escolha(d.chave).arquivo));
  const resumo = !atual
    ? `A versão ${proximo} é a primeira: entra com os ${documentos.length} documentos.`
    : !trocados.length
      ? "Escolha pelo menos um documento pra trocar: publicar sem trocar nada não muda nada."
      : `A versão ${proximo} troca ${juntar(trocados.map((d) => curto(d.titulo)))}${mantidos.length ? ` e mantém ${juntar(mantidos.map((d) => curto(d.titulo)))} da versão ${atual.numero}` : ""}.`;
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Publicar versão nova"
      descricao="Escolha o que troca. O que não trocar continua o mesmo arquivo da versão atual."
      botao={`Publicar versão ${proximo}`}
      podeEnviar={pronto}
      aoAbrir={() => { setEscolhas({}); setRotulo(""); }}
      aoEnviar={async () => {
        const enviados: { documento: string; arquivo_id: string }[] = [];
        for (const d of trocados) {
          const form = new FormData();
          form.append("documento", d.chave);
          form.append("arquivo", escolha(d.chave).arquivo!);
          const r = await enviar<{ id: string }>("/v1/admin/regulamentos/arquivos", form);
          enviados.push({ documento: d.chave, arquivo_id: r.id });
        }
        const g = await postar<{ numero: number }>("/v1/admin/regulamentos/grupos", { trocados: enviados, ...(rotulo.trim() ? { rotulo: rotulo.trim() } : {}) });
        publicado.current = g.numero;
      }}
      aoConcluir={() => aoConcluir(publicado.current)}
    >
      {documentos.map((d) => {
        const e = escolha(d.chave);
        const antes = doAtual(d.chave);
        return (
          <div key={d.chave} className="mtc-pilha" style={{ gap: "var(--e-2)" }}>
            {antes ? (
              <Segmentado rotulo={d.titulo} nome={`regulamento-${d.chave}`} valor={e.trocar ? "trocar" : "manter"} aoMudar={(v) => mudar(d.chave, { trocar: v === "trocar" })} opcoes={[{ valor: "manter", rotulo: "Manter" }, { valor: "trocar", rotulo: "Enviar arquivo novo" }]} />
            ) : (
              <strong style={{ fontSize: "var(--t-sm)" }}>{d.titulo}</strong>
            )}
            {e.trocar ? (
              <CampoArquivo rotulo={`Arquivo novo: ${d.titulo}`} arquivo={e.arquivo} aoEscolher={(arquivo) => mudar(d.chave, { arquivo })} aceita={TIPOS} erro={grande(e.arquivo) ? "O arquivo passou de 10 MB." : undefined} dica="PDF ou texto, até 10 MB." />
            ) : antes ? (
              <span className="mtc-campo__dica">Fica o atual: {antes.arquivo.nome}{antes.herdado_do_grupo ? `, que vem da versão ${antes.herdado_do_grupo}` : `, da versão ${atual!.numero}`}.</span>
            ) : null}
          </div>
        );
      })}
      <Campo rotulo="Rótulo (opcional)" valor={rotulo} aoMudar={setRotulo} maxLength={120} placeholder="Ex.: campanha de outubro" dica="Aparece junto do número da versão, pra equipe e pro cliente." />
      <Aviso tipo={trocados.length || !atual ? "info" : "alerta"}>
        {resumo} A partir da publicação, toda compra nova aceita a versão {proximo}. Quem já comprou continua com a versão da compra dele.
      </Aviso>
    </DialogoDeAcao>
  );
}

type RegulamentoDaMatriculaApi = { grupo: number | null; comprada_em: string | null; versao: { numero: number; rotulo: string | null; publicada_em: string } | null; documentos: (Omit<DocumentoAceito, "href"> & { url: string; pagina?: string })[]; sem_aceite: string | null; versao_sugerida: number | null; oculto_do_cliente?: boolean };

/**
 * "Regulamento desta compra" na ficha da matricula (spec 042, 3.6): a versao que ESTA compra aceitou, com a hora do
 * aceite, como chegou, o `sha256` curto e o arquivo daquela versao. O mesmo que o cliente ve dentro do plano. Sem aceite,
 * o "Registrar regulamento" (spec 046): o backend so manda `versao_sugerida` quando a compra nao tem aceite, e e isso que
 * liga o botao. Aceite que existe nao se reescreve, entao nao ha botao.
 */
export function RegulamentoDaMatricula({ matriculaId, aoConcluir }: { matriculaId: string; aoConcluir?: (texto: string) => void }) {
  const { ler } = useApi();
  const qc = useQueryClient();
  const [registrar, setRegistrar] = useState(false);
  const q = useQuery({ queryKey: ["regulamento-da-matricula", matriculaId], queryFn: () => ler<RegulamentoDaMatriculaApi>(`/v1/admin/matriculas/${matriculaId}/regulamento`) });
  const semAceite = Boolean(q.data && !q.data.documentos.length && q.data.versao_sugerida != null);
  return (
    <Secao rotulo="Regulamento" titulo="Regulamento desta compra">
      {q.data?.oculto_do_cliente && <Aviso tipo="info">Esta versão está escondida da área do cliente: ele não vê este bloco no plano. Mude em Cadastros, Regulamentos.</Aviso>}
      <RegulamentoDaCompra
        titulo={null}
        detalhado
        carregando={q.isLoading}
        erro={q.isError ? erroDe(q.error) : null}
        regulamento={q.data ? { versao: q.data.versao, sem_aceite: q.data.sem_aceite, documentos: q.data.documentos.map(({ url, pagina, ...d }) => ({ ...d, href: linkDoDocumento({ pagina, url }) })) } : null}
        acaoSemAceite={semAceite ? <Botao variante="secundario" tamanho="pequeno" onClick={() => setRegistrar(true)}>Registrar regulamento</Botao> : undefined}
      />
      {q.data && (
        <RegistrarRegulamento
          matriculaId={matriculaId}
          aberto={registrar && semAceite}
          aoFechar={() => setRegistrar(false)}
          sugerida={q.data.versao_sugerida}
          compradaEm={q.data.comprada_em}
          aoConcluir={(n) => {
            void qc.invalidateQueries({ queryKey: ["regulamento-da-matricula", matriculaId] });
            aoConcluir?.(`Regulamento registrado nesta compra: versão ${n}. Fica no seu nome, com o motivo, na ficha e na auditoria.`);
          }}
        />
      )}
    </Secao>
  );
}

/**
 * "Registrar regulamento" (spec 046, 2.3 a 2.6): a versao (vem marcada a que estava no ar no dia da compra) e o motivo,
 * obrigatorio. Quem escolhe os documentos (o geral e o da familia do plano), grava a hora e o nome e recusa o que nao
 * pode e o backend; a recusa aparece na propria janela.
 */
function RegistrarRegulamento({ matriculaId, aberto, aoFechar, sugerida, compradaEm, aoConcluir }: { matriculaId: string; aberto: boolean; aoFechar: () => void; sugerida: number | null; compradaEm: string | null; aoConcluir: (versao: number) => void }) {
  const { ler, postar } = useApi();
  const versoes = useQuery({ queryKey: ["regulamentos-do-painel"], queryFn: () => ler<Regulamentos>("/v1/admin/regulamentos"), enabled: aberto });
  const [versao, setVersao] = useState("");
  const [motivo, setMotivo] = useState("");
  const grupos = [...(versoes.data?.grupos ?? [])].sort((a, b) => b.numero - a.numero);
  const pronto = Boolean(versao) && motivo.trim().length >= 5;
  return (
    <DialogoDeAcao
      aberto={aberto}
      aoFechar={aoFechar}
      titulo="Registrar regulamento"
      descricao="Esta compra entrou sem o aceite. Escolha a versão que vale pra ela: entram o regulamento geral e o da família do plano, como no checkout."
      botao="Registrar regulamento"
      podeEnviar={pronto}
      aoAbrir={() => { setVersao(sugerida != null ? String(sugerida) : ""); setMotivo(""); }}
      aoEnviar={() => postar(`/v1/admin/matriculas/${matriculaId}/regulamento`, { grupo: Number(versao), motivo: motivo.trim() })}
      aoConcluir={() => aoConcluir(Number(versao))}
    >
      {versoes.isLoading ? (
        <Carregando compacto texto="Buscando as versões" />
      ) : versoes.isError ? (
        <Aviso tipo="erro">{erroDe(versoes.error)}</Aviso>
      ) : !grupos.length ? (
        <Aviso tipo="alerta">Nenhuma versão publicada ainda. Publique a primeira em Cadastros, Regulamentos, e volte aqui.</Aviso>
      ) : (
        <ListaDeEscolha
          rotulo="Versão do regulamento"
          nome={`versao-do-regulamento-${matriculaId}`}
          valor={versao}
          aoMudar={setVersao}
          dica={sugerida != null && compradaEm ? `Sugerida: a versão que estava no ar no dia da compra (${formatarData(compradaEm)}).` : undefined}
          opcoes={grupos.map((g) => ({
            valor: String(g.numero),
            titulo: nomeDaVersao(g.numero, g.rotulo),
            detalhe: `Publicada em ${formatarDataHora(g.publicado_em)}${g.situacao === "ATUAL" ? ", no ar hoje" : ""}`,
            selo: g.numero === sugerida ? <Selo tom="acento">Sugerida</Selo> : undefined,
          }))}
        />
      )}
      <CampoTextoLongo rotulo="Motivo" valor={motivo} aoMudar={setMotivo} maxLength={500} contador placeholder="Ex.: cliente confirmou o aceite pelo WhatsApp em 08/10" dica="Pelo menos 5 letras. Fica na ficha e na auditoria." />
      <Aviso tipo="info">O registro fica no seu nome, com a hora de agora, e aparece como registrado pelo administrador, não como aceito pelo cliente. Depois de gravado, não se troca.</Aviso>
    </DialogoDeAcao>
  );
}
